/**
 * SLA Tiers Tab Component
 * Feature: 024-sla-tier-configuration
 *
 * Main tab component for viewing and managing SLA tiers.
 */

import React, { useState, useEffect } from 'react';
import { TierList } from '@components/SLA/TierList';
import { TierForm } from '@components/SLA/TierForm';
import { SLATier } from '@shared/types/sla';
import slaService from '@services/slaService';

export const SLATiersTab: React.FC = () => {
  const [tiers, setTiers] = useState<SLATier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingTier, setEditingTier] = useState<SLATier | null>(null);
  const [includeDeprecated, setIncludeDeprecated] = useState(false);

  useEffect(() => {
    loadTiers();
  }, [includeDeprecated]);

  const loadTiers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await slaService.getTiers({ include_deprecated: includeDeprecated });
      setTiers(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load SLA tiers');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTier = () => {
    setEditingTier(null);
    setShowForm(true);
  };

  const handleEditTier = (tier: SLATier) => {
    setEditingTier(tier);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingTier(null);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingTier(null);
    loadTiers();
  };

  return (
    <div className='space-y-4'>
      <div className='flex justify-between items-center'>
        <div>
          <h2 className='text-2xl font-semibold'>SLA Tiers</h2>
          <p className='text-gray-600 mt-1'>Manage service level agreement tier definitions</p>
        </div>
        <button
          onClick={handleCreateTier}
          className='px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700'
        >
          Create Tier
        </button>
      </div>

      <div className='flex items-center space-x-2'>
        <input
          type='checkbox'
          id='include-deprecated'
          checked={includeDeprecated}
          onChange={(e) => setIncludeDeprecated(e.target.checked)}
          className='rounded'
        />
        <label htmlFor='include-deprecated' className='text-sm text-gray-700'>
          Show deprecated tiers
        </label>
      </div>

      {loading && <div className='text-center py-8'>Loading...</div>}

      {error && (
        <div className='bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded'>
          {error}
        </div>
      )}

      {!loading && !error && (
        <TierList tiers={tiers} onEdit={handleEditTier} onRefresh={loadTiers} />
      )}

      {showForm && (
        <TierForm tier={editingTier} onClose={handleFormClose} onSuccess={handleFormSuccess} />
      )}
    </div>
  );
};

export default SLATiersTab;
